import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useTheme } from '../context/ThemeContext';
import { SOCKET_EVENTS } from '../constants/socketEvents';

interface Order {
  id: number;
  customOrderId?: string;
  orderNumber?: string;
  status: string;
  customerName?: string;
  tableName?: string;
  createdAt: string;
}

interface VendorInfo {
  id: number;
  businessName?: string;
  logoUrl?: string;
}

// Order statuses for each column
const ORDERED_STATUSES = ['PENDING', 'ACCEPTED'];
const PREPARING_STATUSES = ['PREPARING'];
const READY_STATUSES = ['READY'];

const OrderStatusDisplay: React.FC = () => {
  const { user } = useAuth();
  const { socket, isConnected, subscribeToVendor, unsubscribeFromVendor } = useSocket();
  const { theme } = useTheme();
  const [orders, setOrders] = useState<Order[]>([]);
  const [vendorInfo, setVendorInfo] = useState<VendorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const vendorId = user?.vendorId || user?.id;

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Fetch vendor info for logo
  useEffect(() => {
    const fetchVendorInfo = async () => {
      if (!vendorId) return;
      try {
        const response = await api.get<VendorInfo>(`/vendors/${vendorId}`);
        setVendorInfo(response.data);
      } catch (err) {
        console.error('Failed to fetch vendor info:', err);
      }
    };
    fetchVendorInfo();
  }, [vendorId]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!vendorId) return;

    try {
      setError(null);
      const statuses = [...ORDERED_STATUSES, ...PREPARING_STATUSES, ...READY_STATUSES].join(',');
      const response = await api.get<{ content: Order[] } | Order[]>(
        `/vendors/${vendorId}/orders?status=${statuses}&size=100&sort=createdAt,asc`
      );

      const ordersData = Array.isArray(response.data)
        ? response.data
        : response.data.content || [];

      setOrders(ordersData);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setError('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Socket.IO listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected || !vendorId) return;

    subscribeToVendor(vendorId);
    console.log('[OrderStatusDisplay] Subscribed to vendor:', vendorId);

    const handleOrderUpdate = (data: any) => {
      console.log('[OrderStatusDisplay] Received order update:', data);
      fetchOrders();
    };

    socket.on(SOCKET_EVENTS.NEW, handleOrderUpdate);
    socket.on(SOCKET_EVENTS.UPDATED, handleOrderUpdate);
    socket.on(SOCKET_EVENTS.STATUS_UPDATE, handleOrderUpdate);

    return () => {
      socket.off(SOCKET_EVENTS.NEW, handleOrderUpdate);
      socket.off(SOCKET_EVENTS.UPDATED, handleOrderUpdate);
      socket.off(SOCKET_EVENTS.STATUS_UPDATE, handleOrderUpdate);
      unsubscribeFromVendor(vendorId);
      console.log('[OrderStatusDisplay] Unsubscribed from vendor:', vendorId);
    };
  }, [socket, isConnected, vendorId, fetchOrders, subscribeToVendor, unsubscribeFromVendor]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    const displayElement = document.getElementById('order-status-display-container');
    if (!displayElement) return;

    if (!document.fullscreenElement) {
      displayElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Filter orders by column
  const preparingOrders = orders.filter(o =>
    PREPARING_STATUSES.includes(o.status) || ORDERED_STATUSES.includes(o.status)
  );
  const readyOrders = orders.filter(o => READY_STATUSES.includes(o.status));

  // Get display order number
  const getOrderNumber = (order: Order): string => {
    if (order.customOrderId) {
      const match = order.customOrderId.match(/\d+$/);
      return match ? match[0] : order.customOrderId;
    }
    return order.orderNumber || String(order.id);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: theme.background,
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `4px solid ${theme.gray[200]}`,
          borderTop: `4px solid ${theme.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Render order number grid
  const renderOrderGrid = (ordersList: Order[], columnColor: string) => {
    if (ordersList.length === 0) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: isMobile ? '120px' : '200px',
          color: theme.gray[400],
          fontSize: isMobile ? '1rem' : '1.25rem',
        }}>
          No orders
        </div>
      );
    }

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? 'repeat(auto-fill, minmax(70px, 1fr))'
          : 'repeat(auto-fill, minmax(90px, 1fr))',
        gap: isMobile ? '8px' : '12px',
        padding: isMobile ? '16px' : '20px',
        alignContent: 'start',
      }}>
        {ordersList.map(order => (
          <div
            key={order.id}
            style={{
              background: columnColor,
              borderRadius: '12px',
              padding: isMobile ? '12px 8px' : '16px 12px',
              textAlign: 'center',
              fontSize: isMobile ? '1.25rem' : '1.75rem',
              fontWeight: '700',
              color: 'var(--text-inverse)',
              fontFamily: "'Roboto Mono', monospace",
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            {getOrderNumber(order)}
          </div>
        ))}
      </div>
    );
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div style={{
        minHeight: '100vh',
        background: theme.background,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Mobile Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          background: theme.components.card.background,
          borderBottom: `1px solid ${theme.components.card.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {vendorInfo?.logoUrl && (
              <img
                src={vendorInfo.logoUrl}
                alt=""
                style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover' }}
              />
            )}
            <span style={{ color: theme.text, fontSize: '1.125rem', fontWeight: '600' }}>
              {vendorInfo?.businessName || 'Order Status'}
            </span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: isConnected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            borderRadius: '20px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: isConnected ? '#10b981' : '#ef4444',
            }} />
            <span style={{
              color: isConnected ? '#10b981' : '#ef4444',
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              {isConnected ? 'LIVE' : 'OFFLINE'}
            </span>
          </div>
        </div>

        {/* Mobile - Preparing Section */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{
            background: theme.components.card.background,
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.components.card.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '3px',
                background: 'var(--status-warn)',
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '1rem',
                fontWeight: '600',
                color: theme.text,
              }}>
                Preparing
              </h2>
            </div>
            <span style={{
              background: 'var(--status-warn)',
              padding: '4px 12px',
              borderRadius: '20px',
              color: 'var(--text-inverse)',
              fontSize: '0.875rem',
              fontWeight: '700',
            }}>
              {preparingOrders.length}
            </span>
          </div>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            background: theme.background,
          }}>
            {renderOrderGrid(preparingOrders, '#f59e0b')}
          </div>
        </div>

        {/* Mobile - Ready Section */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{
            background: theme.components.card.background,
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.components.card.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '3px',
                background: 'var(--action)',
              }} />
              <h2 style={{
                margin: 0,
                fontSize: '1rem',
                fontWeight: '600',
                color: theme.text,
              }}>
                Ready to Collect
              </h2>
            </div>
            <span style={{
              background: 'var(--action)',
              padding: '4px 12px',
              borderRadius: '20px',
              color: 'var(--text-inverse)',
              fontSize: '0.875rem',
              fontWeight: '700',
            }}>
              {readyOrders.length}
            </span>
          </div>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            background: theme.background,
          }}>
            {renderOrderGrid(readyOrders, '#10b981')}
          </div>
        </div>

        {error && (
          <div style={{
            position: 'fixed',
            bottom: '16px',
            left: '16px',
            right: '16px',
            padding: '12px',
            background: 'rgba(239, 68, 68, 0.95)',
            borderRadius: '8px',
            color: 'var(--text-inverse)',
            fontSize: '0.875rem',
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@500;700&display=swap');
        `}</style>
      </div>
    );
  }

  // Desktop Layout
  return (
    <>
      {/* Non-fullscreen header */}
      {!isFullscreen && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          background: theme.components.card.background,
          borderBottom: `1px solid ${theme.components.card.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: theme.text }}>
            Order Status Display
          </h1>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {error && (
              <span style={{ color: 'var(--status-bad)', fontSize: '0.875rem' }}>{error}</span>
            )}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: isConnected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderRadius: '20px',
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: isConnected ? '#10b981' : '#ef4444',
              }} />
              <span style={{ color: isConnected ? '#10b981' : '#ef4444', fontSize: '0.875rem', fontWeight: '500' }}>
                {isConnected ? 'Live' : 'Offline'}
              </span>
            </div>
            <button
              onClick={toggleFullscreen}
              style={{
                padding: '10px 20px',
                background: theme.primary,
                border: 'none',
                borderRadius: '8px',
                color: 'var(--text-inverse)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
            >
              Fullscreen
            </button>
          </div>
        </div>
      )}

      {/* Main Display Container */}
      <div
        id="order-status-display-container"
        style={{
          display: 'flex',
          height: isFullscreen ? '100vh' : 'calc(100vh - 73px)',
          width: '100%',
          background: theme.background,
        }}
      >
        {/* Order Columns Container - 80% width */}
        <div style={{
          flex: '0 0 80%',
          display: 'flex',
          padding: '20px',
          gap: '20px',
        }}>
          {/* Preparing Column */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: theme.components.card.background,
            borderRadius: '16px',
            border: `1px solid ${theme.components.card.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${theme.components.card.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '4px',
                  background: 'var(--status-warn)',
                }} />
                <h2 style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: theme.text,
                }}>
                  Preparing
                </h2>
              </div>
              <span style={{
                background: 'var(--status-warn)',
                padding: '6px 16px',
                borderRadius: '20px',
                color: 'var(--text-inverse)',
                fontSize: '1rem',
                fontWeight: '700',
              }}>
                {preparingOrders.length}
              </span>
            </div>
            {/* Order Grid */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {renderOrderGrid(preparingOrders, '#f59e0b')}
            </div>
          </div>

          {/* Ready Column */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: theme.components.card.background,
            borderRadius: '16px',
            border: `1px solid ${theme.components.card.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${theme.components.card.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '4px',
                  background: 'var(--action)',
                }} />
                <h2 style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: theme.text,
                }}>
                  Ready to Collect
                </h2>
              </div>
              <span style={{
                background: 'var(--action)',
                padding: '6px 16px',
                borderRadius: '20px',
                color: 'var(--text-inverse)',
                fontSize: '1rem',
                fontWeight: '700',
              }}>
                {readyOrders.length}
              </span>
            </div>
            {/* Order Grid */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {renderOrderGrid(readyOrders, '#10b981')}
            </div>
          </div>
        </div>

        {/* Branding Area - 20% width */}
        <div style={{
          flex: '0 0 20%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          background: theme.components.card.background,
          borderLeft: `1px solid ${theme.components.card.border}`,
          position: 'relative',
        }}>
          {vendorInfo?.logoUrl ? (
            <img
              src={vendorInfo.logoUrl}
              alt={vendorInfo.businessName || 'Restaurant Logo'}
              style={{
                maxWidth: '80%',
                maxHeight: '160px',
                objectFit: 'contain',
                marginBottom: '20px',
              }}
            />
          ) : (
            <div style={{
              width: '100px',
              height: '100px',
              background: theme.gray[100],
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
            }}>
              <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke={theme.gray[400]} strokeWidth="1.5">
                <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
              </svg>
            </div>
          )}

          {vendorInfo?.businessName && (
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: theme.text,
              textAlign: 'center',
            }}>
              {vendorInfo.businessName}
            </h3>
          )}

          <p style={{
            margin: 0,
            fontSize: '0.875rem',
            color: theme.gray[500],
            textAlign: 'center',
          }}>
            Thank you for your order
          </p>

          {/* Connection status in fullscreen */}
          {isFullscreen && (
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: isConnected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderRadius: '20px',
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: isConnected ? '#10b981' : '#ef4444',
              }} />
              <span style={{
                color: isConnected ? '#10b981' : '#ef4444',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                {isConnected ? 'LIVE' : 'OFFLINE'}
              </span>
            </div>
          )}
        </div>

        {/* CSS */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@500;700&display=swap');

          #order-status-display-container:fullscreen {
            width: 100vw !important;
            height: 100vh !important;
          }

          /* Scrollbar styling */
          #order-status-display-container ::-webkit-scrollbar {
            width: 6px;
          }
          #order-status-display-container ::-webkit-scrollbar-track {
            background: transparent;
          }
          #order-status-display-container ::-webkit-scrollbar-thumb {
            background: ${theme.gray[300]};
            border-radius: 3px;
          }
          #order-status-display-container ::-webkit-scrollbar-thumb:hover {
            background: ${theme.gray[400]};
          }
        `}</style>
      </div>
    </>
  );
};

export default OrderStatusDisplay;
